"use client";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.backToHome')}
        </Link>

        {/* Content card */}
        <article className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 p-6 sm:p-8 lg:p-12">
          {/* Centered title */}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-8 uppercase tracking-wide">
            УГОДА КОРИСТУВАЧА
          </h1>

          <div className="text-gray-700 text-base leading-relaxed space-y-6">
            {/* Intro paragraph - italic */}
            <p className="italic text-center">
              Сервіс CONFY, і третя особа, яка бажає придбати Електронний квиток на Події через Сайт Сервісу https://confy.cc або його піддомени, уклали цю Угоду про наведене нижче.
            </p>

            {/* Section 1 */}
            <section>
              <h2 className="font-bold text-lg mb-4">1. Терміни та визначення</h2>

              <p className="mb-4 pl-8">
                1.1. У цій Угоді, якщо з тексту прямо не випливає інше, наступні терміни матимуть наступні значення:
              </p>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  <strong>Електронний квиток</strong> – документ, сформований Сервісом в електронній цифровій формі, що містить інформацію, за допомогою якої ідентифікується особа Покупця та Подія Організатора, на участь у якій на на підставі даного Електронного квитка Покупець має право. Реалізований Сервісом Електронний квиток на Подію Організатора створює відповідні обов&apos;язки для Організатора, а саме - забезпечити перепустку Покупця на Подію та надати послугу з проведення Події;
                </p>

                <p className="text-justify">
                  <strong>Замовлення (Реєстрація)</strong> – належним чином оформлений за допомогою Сайту запит Покупця (Користувача) на участь у Події Організатора, який свідчить про намір Користувача укласти з Організатором на Сайті Угоду про надання послуг, встановлює обов&apos;язок Організатора провести Події та право Користувача та/або відвідати Подію у вказані в Електронному квитку дату, час та місце.
                </p>

                <p className="text-justify">
                  <strong>Подія</strong> – Подія (послуга), у тому числі конференція, семінар, виставка, показ, курси, майстер-клас, тренінг, яку організовує та проводить Організатор та інформацію про який він розмістив на Сайті через власний Особистий кабінет з метою реєстрації Покупців та реалізації ним електронних квитків;
                </p>

                <p className="text-justify">
                  <strong>Особистий кабінет</strong> – сукупність захищених сторінок Сайту, які були створені внаслідок введення Організатором або Покупцем своїх даних у реєстраційну форму на Сайті та за допомогою яких вони мають можливість подавати Заявку організатора, змінювати особисті дані, зареєструватися на Події, здійснювати оплату за Електронні квитки та використовувати інші можливості сайту. Для кожної фізичної особи, фізичної особи-підприємця та юридичної особи, які реєструються на Сайті як Організатор або Покупець, реєструється окремий Особистий кабінет. У разі здійснення реєстрації на безкоштовну Подію та/або придбання Електронних квитків на платну Подію зі свого Особистого кабінету Покупцем для інших осіб, кожна з таких осіб набуває статусу Покупця, проте відповідальність за достовірність та правильність даних, зазначених при реєстрації таких Покупців, несе особа, з Особистого кабінету якого реєстрацію та/або придбання Електронних квитків було здійснено;
                </p>

                <p className="text-justify">
                  <strong>Організатор</strong> – фізична особа-підприємець або юридична особа, яка здійснює реєстрацію та/або реалізацію Електронних квитків на свою Події за допомогою Сервісу;
                </p>

                <p className="text-justify">
                  <strong>Користувач (Покупець)</strong> – фізична особа, якій створено Особистий кабінет на Сайті шляхом введення Користувачем своїх особистих та контактних даних у відповідну форму на Сайті. Користувач може формувати запити на Замовлення участі у Події, отримувати підтвердження таких Замовлень та отримувати сформовані Електронні квитки на Події організатори через Сервіс;
                </p>

                <p className="text-justify">
                  <strong>Сервіс</strong> – онлайн-сервіс реєстрації та попереднього продажу електронних квитків CONFY – сукупність сторінок Сайту, розміщених за постійною адресою в мережі Інтернет https://confy.cc або піддоменах, що надаються Сервісом у користування Організатору та Покупцям з набором функцій, необхідних для реєстрації та/або реалізації Покупцям Електронних квитків на Події Організаторів;
                </p>

                <p className="text-justify">
                  <strong>Сайт</strong> – Сайт https://confy.cc;
                </p>

                <p className="text-justify">
                  <strong>Push-повідомлення</strong> – інформаційні повідомлення, сформовані Організатором для Покупців через відповідний функціонал Сервісу з метою інформування їх про зміни, що стосуються Події, надсилання іншої важливої, на його думку, інформації. Можуть надсилатися на електронну адресу, вказану Покупцями під час реєстрації, або у СМС-повідомленнях.
                </p>
              </div>

              <p className="mt-4 pl-8 text-justify">
                1.3. Усі інші терміни, що зустрічаються в тексті Угоди, за відсутності їх тлумачення у цій Угоді, трактуються відповідно до законодавства України, що діє на момент укладання договору та додаткових угод до нього.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-bold text-lg mb-4">2. Предмет Угоди</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  2.1. У порядку та на умовах, передбачених Угодою, Сервіс надає Користувачеві можливість використання Сайту, а Користувач зобов&apos;язується за необхідності використовувати Сайт відповідно до умов Угоди.
                </p>

                <p className="text-justify">
                  2.2. На підставі Угоди Користувач має можливість використовувати Сайт для отримання розміщеної на ній інформації про Події, а також можливість брати участь у Подіях, оформивши замовлення, здійснивши дії, передбачені Організатором (зокрема, зареєструвавшись на Події та, за необхідності, сплатити за цю участь) та отримавши Електронний квиток від Сервісу. У випадках, передбачених відповідними Угодами між Сервісом та Організатором, Сервіс, діючи від імені та за рахунок Організатора, здійснює реалізацію Користувачам Електронних Квитків.
                </p>

                <p className="text-justify">
                  2.3. Якщо інше прямо не передбачено Угодою між Сторонами, Сервіс не є організатором подій, продавцем будь-яких товарів чи послуг. Усі зобов&apos;язання щодо проведення подій, купівлі-продажу відповідних товарів або надання відповідних послуг виникають між Користувачем та Організатором та/або третіми особами. Якщо інше прямо не передбачено Угодою між Сторонами, Сервіс не несе відповідальності за дійсність та виконання зазначених зобов&apos;язань. При цьому у випадку, якщо Користувач купує Електронний квиток шляхом перерахування коштів через Сайт Сервісу, Сервіс бере на себе зобов&apos;язання видати Користувачеві Електронний квиток шляхом його відправлення на адресу електронної пошти та номер телефону, вказані Користувачем під час реєстрації на Сайті.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-bold text-lg mb-4">3. Права та обов&apos;язки Сервісу</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  3.1. Сервіс має право заблокувати доступ до Сайту для Користувача, якщо він своїми діями чи бездіяльністю порушує умови Угоди, інші Угоди між Сторонами або інші вимоги Сервісу.
                </p>

                <p className="text-justify">
                  3.2. Сервіс має право проводити модифікацію будь-якого програмного забезпечення Сайту, припиняти роботу Сайту, при виявленні суттєвих несправностей, помилок та збоїв, а також з метою проведення профілактичних робіт та запобігання випадкам несанкціонованого доступу Сайту.
                </p>

                <p className="text-justify">
                  3.3. Сервіс має право використовувати дані Користувача або інших осіб, інформація про які внесена Користувачем на Сайт, у маркетингових цілях. Сервіс гарантує, що дані, внесені Користувачем на Сайт, є добровільно наданими ним та третіми особами та треті особи ознайомлені з умовами Угоди.
                </p>

                <p className="text-justify">
                  3.4. Сервіс має право надсилати Користувачеві та іншим особам, інформація про яких внесена Користувачем на Сайт, інформаційні та рекламні повідомлення на електронну адресу або за номером мобільного телефону, що вказані при реєстрації на Сайті. Цією Угодою Користувач дає згоду на отримання подібної інформації, також гарантує, що аналогічну згоду надано і особами, відомості про які Користувач вніс на Сайт.
                </p>

                <p className="text-justify">
                  3.5. Сервіс має право консультувати Користувача під час оформлення/підтвердження/оплати Замовлення. В тому числі самостійно зв&apos;язуватися з Користувачем за вказаними Користувачем телефонами.
                </p>

                <p className="text-justify">
                  3.6. Сервіс має право вибирати самостійно та на свій розсуд, встановлювати способи ідентифікації Користувача на всіх своїх ресурсах, у тому числі Сайтах, серверах та інших.
                </p>

                <p className="text-justify">
                  3.7. Сервіс зобов&apos;язаний інформувати Користувача про статус Замовлення та факт надходження оплати за нього. Фактом того, що Користувач обізнаний, є факт надсилання відповідного електронного листа на електронну адресу, вказану Користувачем під час реєстрації на Сайті.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="font-bold text-lg mb-4">4. Права та обов&apos;язки Користувача</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  4.1. Користувач зобов&apos;язується знайомитись з актуальною версією Угоди при кожному відвідуванні Сайту до моменту користування Сайтом.
                </p>

                <p className="text-justify">
                  4.2. Користувач зобов&apos;язується дотримуватися Правил користування Сайтом.
                </p>

                <p className="text-justify">
                  4.3. Користувач зобов&apos;язується надавати достовірну та, у разі потреби, повну інформацію при використанні Сайту.
                </p>

                <p className="text-justify">
                  4.4. Користувач зобов&apos;язується самостійно перевірити дані Замовлення перед його оформленням. Користувач несе повну відповідальність за достовірність та правомірність використання даних, використаних ним під час оформлення Замовлення.
                </p>

                <p className="text-justify">
                  4.5. Користувач зобов&apos;язується ознайомитись із змістом, умовами реєстрації та порядком проведення Події, а також з додатковими вимогами, що пред&apos;являються Організатором до оформлення Замовлення. Якщо Користувачеві не повністю зрозумілі будь-які умови проведення Події, в тому числі порядок оплати, Користувач зобов&apos;язується уточнити ці умови Організатора, а при неможливості уточнення відмовитися від оформлення Замовлення та відвідування Події.
                </p>

                <p className="text-justify">
                  4.6. Користувач має право вибирати, із запропонованих на Сайті варіантів, спосіб оплати участі у Події. У цьому Користувач зобов&apos;язується самостійно ознайомитися з правилами користування обраним ним способом платежу.
                </p>

                <p className="text-justify">
                  4.7. Користувач має право відмовитися від участі в Події після оформлення та оплати Електронного квитка на умовах та в порядку, передбаченому Організатором. У будь-якому випадку, повернення коштів може відбуватися лише за умови відрахування з суми до повернення всіх комісійних та банківських витрат, понесених Сторонами.
                </p>

                <p className="text-justify">
                  4.8. Користувач має право вимагати повернення коштів у разі скасування Події. У цьому випадку він повинен звернутися до Організатора, оскільки Сервіс надає виключно посередницькі послуги з реалізації квитків від імені та за рахунок Організатора.
                </p>

                <p className="text-justify">
                  4.9. Сервіс за жодних обставин не несе відповідальності за виконання покладених на нього Організатором обов&apos;язків щодо повернення коштів Користувачеві у разі відмови Користувача від участі у Події або скасування або перенесення Події Виконавцем. Усі претензії, пов&apos;язані із зазначеними діями та Подіями Користувач повинен надсилати безпосередньо за вказаними на Сайті реквізитами Організатора, або за реквізитами Організатора, взятими з відкритих реєстрів. У разі неможливості для Користувача встановити реквізити Організатора, Сервіс за запитом Користувача надає наступні реквізити Користувачеві за допомогою Особистого кабінету або за адресою електронної пошти Користувача.
                </p>

                <p className="text-justify">
                  4.10. Користувач зобов&apos;язується сплатити у повному обсязі самостійно або через третіх осіб вартість Замовлення. Після оплати Замовлення, Електронний квиток вважається проданим, і Користувач має право відвідати Подію або скористатися іншою послугою Організатора.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="font-bold text-lg mb-4">5. Порядок оформлення Замовлення та отримання Електронного квитка</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  5.1. Користувач, який бажає відвідати Подію або скористатися іншою послугою Організатора, інформацію про яку розміщено на Сайті, вивчає інформацію про Події або послуги на відповідній сторінці Сайту.
                </p>

                <p className="text-justify">
                  5.2. Для оформлення Замовлення Користувач має вжити таких дій:
                </p>

                <p className="text-justify pl-8">
                  5.2.1. надати передбачені на сторінці реєстрації на Події відомості про себе (надані відомості повинні відповідати дійсності та надаватись у необхідному обсязі);
                </p>

                <p className="text-justify pl-8">
                  5.2.2. підтвердити коректність внесених відомостей та бажання оформити замовлення натисканням кнопки «Продовжити» або «Зареєструватися» на відповідній сторінці Сайту.
                </p>

                <p className="text-justify">
                  5.3. Сервіс підтверджує прийняття та оформлення Замовлення шляхом надсилання електронною поштою Користувача, зазначеного при оформленні Замовлення, відповідного повідомлення.
                </p>

                <p className="text-justify">
                  5.4. Якщо участь у Події для Користувача безкоштовна, а Організатором не встановлено додаткових вимог для участі у Події, Сервіс надсилає Користувачеві повідомлення з Електронним квитком, що підтверджує можливість участі у Події.
                </p>

                <p className="text-justify">
                  5.5. Якщо для участі в Події Користувач повинен сплатити встановлену вартість Замовлення після його попереднього підтвердження, Сайт перенаправляє Користувача на сторінку оплати Замовлення. Для оплати Замовлення Користувач вибирає відповідний спосіб оплати, вказаний на сторінці Сайту та здійснює оплату відповідно до правил здійснення платежів обраного Користувачем способу оплати. Після оплати Замовлення Сервіс формує та надсилає Користувачеві повідомлення з Електронним квитком як факт підтвердження можливості участі у Події Організатора.
                </p>

                <p className="text-justify">
                  5.6. Якщо Організатором встановлено додаткові умови участі у Події та налаштовано додаткову модерацію для підтвердження Заявки, Електронний квиток, як факт підтвердження можливості участі у Події Організатора, буде надіслано Сервісом на електронну пошту Користувача лише після натискання відповідної кнопки підтвердження в Особистому кабінеті.
                </p>

                <p className="text-justify">
                  5.7. Моментом оплати Замовлення є момент надходження коштів на Рахунок. З моменту оплати Замовлення між Організатором та Користувачем виникають передбачені умовами Події зобов&apos;язання. З того ж моменту у Сервісу виникає зобов&apos;язання щодо видачі Користувачеві Електронного Квитка.
                </p>

                <p className="text-justify">
                  5.8. У разі внесення за Користувача платежу третіми особами всі права та обов&apos;язки за цим платежем виникають у Користувача, і такі дії третіх осіб оцінюються Сторонами як вчинені зазначеними третіми особами на користь Користувача та не породжують жодних прав та обов&apos;язків між зазначеними третіми особами та Сервісом.
                </p>

                <p className="text-justify">
                  5.9. Користувач бере участь у Події відповідно до правил, встановлених Організатором.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="font-bold text-lg mb-4">6. Відповідальність Сторін</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  6.1. Інформація, що розміщується на Сайті, додається на Сайті Організаторами. Сервіс не має технічної можливості та не зобов&apos;язаний контролювати розміщену інформацію та піддавати її обов&apos;язковій перевірці та затвердженню. Сервіс не несе відповідальності за достовірність, точність, повноту або якість будь-якої інформації, що публікується Організаторами та/або третіми особами на Сайті. Сервіс не підтримує та не підтверджує жодної інформації, розміщеної Організаторами та/або третіми особами на Сайті. Користувач розуміє та погоджується, що, використовуючи Сайт, він може бачити інформацію, яка є образливою, негідною чи спірною.
                </p>

                <p className="text-justify">
                  6.2. Сервіс не має можливості проводити перевірку інформації розміщеної Організаторами та не може гарантувати повну відсутність неточностей у ній, у зв&apos;язку з чим не несе перед Користувачем відповідальності за будь-які помилкові та/або недостовірні дані про Події та послуги, так само як і у заподіяну Користувачеві шкоду та/або збитки через наявність помилок в інформації.
                </p>

                <p className="text-justify">
                  6.3. Будь-які матеріали, отримані з використанням Сайту, Користувач може використовувати на свій страх та ризик, на нього покладається відповідальність за будь-які збитки, які можуть бути завдані внаслідок використання цих матеріалів. Сервіс за жодних обставин не несе відповідальності перед Користувачем або третіми особами за будь-які збитки, спричинені використанням Сайту, вмісту Сайту або інших матеріалів, до яких було отримано доступ із Сайту.
                </p>

                <p className="text-justify">
                  6.4. Сервіс не несе відповідальності за некоректну поведінку осіб, які використовують Сайт.
                </p>

                <p className="text-justify">
                  6.5. Сервіс не гарантує, що:
                </p>

                <p className="text-justify pl-8">
                  6.5.1. Сайт відповідатиме вимогам Користувача;
                </p>

                <p className="text-justify pl-8">
                  6.5.2. результати, які можуть бути отримані з використанням Сайту, будуть точними та надійними;
                </p>

                <p className="text-justify pl-8">
                  6.5.3. якість будь-якого продукту, послуги, інформації, отриманої з використанням Сайту, відповідатиме очікуванням Користувача;
                </p>

                <p className="text-justify pl-8">
                  6.5.4. Сайт працюватиме безперервно, швидко, надійно та без помилок та відповідатиме очікуванням Користувача;
                </p>

                <p className="text-justify pl-8">
                  6.5.5. усі помилки на Сайті будуть виправлені.
                </p>

                <p className="text-justify">
                  6.6. Сайт або його елементи періодично можуть бути частково або повністю недоступні через проведення профілактичних або інших робіт технічного характеру, що забезпечують нормальне функціонування Сайту. У такому разі Сервіс не несе зобов&apos;язань щодо повідомлення Користувачів або неодержання ними інформації.
                </p>

                <p className="text-justify">
                  6.7. Сервіс не несе відповідальності за недотримання Організатором умов проведення Подій або надання послуг, оскільки ці умови та правила знаходяться у винятковому підпорядкуванні Організатора.
                </p>

                <p className="text-justify">
                  6.8. Сервіс не несе відповідальності за порушення порядку та строків здійснення повернення квитків/коштів, якщо воно не викликане діями або бездіяльністю безпосередньо Сервісу, у тому числі, але не обмежуючись, у разі дій чи бездіяльності третіх осіб, ненадання Користувачем необхідних для здійснення повернення даних.
                </p>

                <p className="text-justify">
                  6.9. Користувач бере на себе повну відповідальність та ризики, пов&apos;язані з оформленням Замовлень. Користувач несе одноосібну відповідальність за будь-яку, що виникла в процесі реєстрації, оформлення, підтвердження та оплати Замовлення. У такому разі Сервіс не несе відповідальності за будь-які збитки (грошового чи іншого характеру), що виникли внаслідок проблеми, спричиненої або обумовленої помилкою або недбалістю Користувача при використанні Сайту, у тому числі неуспішним завершенням або підтвердженням будь-якої операції.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="font-bold text-lg mb-4">7. Персональні дані</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  7.1. При реєстрації на Подія та оформлення Замовлення Користувач вносить свої персональні дані у форму на Сайті або у форму Сервісу, розташовану на іншому Сайті в мережі Інтернет, Користувач дає свою згоду на обробку персональних даних як без використання засобів автоматизації, так і з їх використанням. Зазначена згода надається Користувачем Сервісу та Організатору.
                </p>

                <p className="text-justify">
                  7.2. Користувач підтверджує, що він передає свої персональні дані для обробки Сервісу та відповідному Організатору та погоджується на їх обробку. Користувач також повідомлений, що обробка його персональних даних здійснюватиметься Сервісом та Організатором на підставі Закону України про захист персональних даних №2297-VI від 01.06.2010 р. У зв&apos;язку з чим Користувач за необхідності видає згоду на обробку персональних даних.
                </p>

                <p className="text-justify">
                  7.3. Користувач дає Сервісу та Організатору згоду на обробку таких персональних даних:
                </p>

                <p className="text-justify pl-8">7.3.1. прізвище ім&apos;я по батькові;</p>
                <p className="text-justify pl-8">7.3.2. номери контактних телефонів;</p>
                <p className="text-justify pl-8">7.3.3. адреси електронної пошти;</p>
                <p className="text-justify pl-8">7.3.4. місце роботи та посада;</p>
                <p className="text-justify pl-8">7.3.5. дані про свої інтереси;</p>
                <p className="text-justify pl-8">7.3.6. дані браузера, пристрої та IP-адреса;</p>
                <p className="text-justify pl-8">7.3.7. інші дані, які може встановити Організатор як обов&apos;язкові для заповнення.</p>

                <p className="text-justify">
                  7.4. Метою обробки персональних даних є виконання Сервісом та Організатором відповідних договорів, а також проведення необхідних маркетингових заходів, у тому числі, але не обмежуючись, здійснення інформаційної розсилки (про Події, акції та ін.) на адресу електронної пошти або за допомогою СМС повідомлень.
                </p>

                <p className="text-justify">
                  7.5. У ході обробки з персональними даними будуть здійснені такі дії: збирання, записування, систематизація, накопичення, зберігання, уточнення (оновлення, зміна), вилучення, використання, передача (поширення, надання, доступ), знеособлення, блокування, видалення, знищення.
                </p>

                <p className="text-justify">
                  7.6. Передача персональних даних третім особам здійснюється відповідно до чинного законодавства України та Угоди між Сторонами.
                </p>

                <p className="text-justify">
                  7.7. Реєструючись на Події, розміщені на Сайті Користувач погоджується, що його персональні дані стають доступними Організатору цієї Події.
                </p>

                <p className="text-justify">
                  7.8. Персональні дані обробляються до моменту ліквідації Сервісу або до ліквідації Сайту (залежно від того, яка Подія настане пізніше). Персональні дані обробляються Організатором у порядку, передбаченому Організатором. Зберігання персональних даних здійснюється відповідно до чинного законодавства України.
                </p>

                <p className="text-justify">
                  7.9. Згода на обробку персональних даних Сервісом може бути відкликана Користувачем шляхом надсилання письмової заяви представнику Сервісу на електронну адресу support@confy.cc.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="font-bold text-lg mb-4">8. Вирішення спорів</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  8.1. Ця Угода та всі правовідносини, що її виникають, регулюються законодавством України.
                </p>

                <p className="text-justify">
                  8.2. Усі суперечки, розбіжності та претензії, які можуть виникнути у зв&apos;язку з виконанням, розірванням або визнанням угоди недійсною, сторони прагнутимуть вирішити шляхом переговорів. Сторона, у якої виникли претензії та/або розбіжності, надсилає іншій Стороні повідомлення із зазначенням виникли претензій та/або розбіжностей повідомлення.
                </p>

                <p className="text-justify">
                  8.3. Вказане в пункті 8.2 Угоди повідомлення надсилається Користувачем електронною поштою на адресу support@confy.cc, а також надсилається Сервісу письмово за допомогою надсилання поштою рекомендованим листом з повідомленням про вручення. Повідомлення має містити суть висунутих вимог, докази, що підтверджують вимогу, а також інформацію про Користувача.
                </p>

                <p className="text-justify">
                  8.4. Протягом 15 (п&apos;ятнадцяти) робочих днів з моменту отримання зазначеного у пункті 8.2 Угоди повідомлення, за умови, що це повідомлення відповідає положенням пункту 8.3 Угоди, Сторона, яка одержала його, має направити відповідь на це повідомлення.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="font-bold text-lg mb-4">9. Набуття чинності Угодою та зміною умов Угоди</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  9.1. Текст Угоди, постійно розміщений в мережі Інтернет за адресою https://confy.cc, містить усі істотні умови Угоди та є пропозицією Сервісу укласти Угоду з Користувачем на зазначених у тексті Угоди умов. Таким чином текст Угоди є публічною офертою.
                </p>

                <p className="text-justify">
                  9.2. Належним акцептом цієї оферти вважається послідовне здійснення будь-якою третьою особою таких дій:
                </p>

                <p className="text-justify pl-8">9.2.1. ознайомлення з умовами Угоди;</p>

                <p className="text-justify pl-8">
                  9.2.2. введення реєстраційних даних у формі реєстрації та підтвердження достовірності цих даних натисканням кнопки «Продовжити» або «Зареєструватися».
                </p>

                <p className="text-justify">
                  9.3. З моменту натискання кнопки «Продовжити», або «Зареєструватися» відповідно та у разі правильного послідовного виконання всіх зазначених вище дій, Угода вважається укладеною.
                </p>

                <p className="text-justify">
                  9.4. Угода може бути розірвана у будь-який час за ініціативою кожної із Сторін. Для цього Сервіс розміщує повідомлення про розірвання Угоди на Сайті та/або надсилає Користувачеві відповідне повідомлення, з моменту такого розміщення/напряму повідомлення Угода вважається розірваною. Користувач може розірвати, надіславши Сервісу електронною поштою на адресу support@confy.cc повідомлення про розірвання Угоди.
                </p>

                <p className="text-justify">
                  9.5. Сторони погоджуються, що Угода може бути змінена Сервісом в односторонньому порядку шляхом розміщення оновленого тексту Угоди в Інтернеті за адресою https://confy.cc. Користувач підтверджує свою згоду зі змінами умов цієї Угоди шляхом використання Сайту Сервісу. У разі незгоди зі зміненою версією Угоди, Користувач припиняє користування Сайтом Сервісу.
                </p>

                <p className="text-justify">
                  9.6. Користувач, який не погоджується з умовами Угоди та/або зі зміною умов Угоди, повинен негайно розірвати Угоду у порядку, передбаченому пунктом 9.4 Угоди.
                </p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="font-bold text-lg mb-4">10. Правова інформація</h2>

              <div className="pl-8 space-y-2">
                <p className="text-justify">
                  Правовласником та власником платформи CONFY є ТОВ &quot;Медріс Груп&quot;.
                </p>

                <p><strong>Код ЄДРПОУ:</strong> 37911585</p>

                <p><strong>Юридична адреса:</strong> вул. Петра Сагайдачного, 12, м. Київ, 04070, Україна</p>

                <p>
                  <strong>Адреса для направлення запитів:</strong>{" "}
                  <a href="mailto:info@confy.cc" className="text-blue-600 hover:underline">
                    info@confy.cc
                  </a>
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}
